<script setup>
import { ref, reactive } from 'vue'
import { storeToRefs } from 'pinia'
import { useAdminCenterStore } from '../../stores/AdminCenterStore'
import { useSortedBatch } from '../../composables/useSortedBatch'
import { sortUserRows, DEFAULT_USER_SORT, USER_SORT_KEYS } from '../../utils/adminCenterTableSort'
import { isAgencyAdminUser } from '../../access/roleKeys'
import { handleErrors } from '../../utils/errorHandler'
import BcsCard from '../common/BcsCard.vue'
import BcsStatPill from '../common/BcsStatPill.vue'
import BcsStatusPill from '../common/BcsStatusPill.vue'
import BcsDropdownActions from '../common/BcsDropdownActions.vue'
import AddAccountUserModal from './AddAccountUserModal.vue'
import ConfirmActionModal from './ConfirmActionModal.vue'
import RemoveUserModal from './RemoveUserModal.vue'

const adminCenterStore = useAdminCenterStore()
const {
  isLoading,
  activeUsers,
  pendingUsers,
  totalActive,
  totalPending
} = storeToRefs(adminCenterStore)

const ACTIVE_COLUMNS = [
  { id: 'userName', label: 'User Name', sortKey: USER_SORT_KEYS.NAME, width: '25%', thClass: 'ps-4' },
  { id: 'title', label: 'Title', sortKey: USER_SORT_KEYS.TITLE, width: '25%' },
  { id: 'employers', label: 'Employers', sortKey: USER_SORT_KEYS.EMPLOYERS, width: '14%', thClass: 'text-center' },
  { id: 'accountStatus', label: 'Account Status', sortKey: null, width: '30%', thClass: 'text-center' },
  { id: 'actions', label: 'Actions', sortKey: null, width: '6%', thClass: 'text-center', hiddenLabel: true }
]

const PENDING_COLUMNS = [
  { id: 'userName', label: 'User Name', sortKey: USER_SORT_KEYS.NAME, width: '25%', thClass: 'ps-4' },
  { id: 'title', label: 'Title', sortKey: USER_SORT_KEYS.TITLE, width: '25%' },
  { id: 'inviteSent', label: 'Invite Sent', sortKey: USER_SORT_KEYS.INVITE_SENT, width: '14%' },
  { id: 'accountStatus', label: 'Account Status', sortKey: null, width: '30%', thClass: 'text-center' },
  { id: 'actions', label: 'Actions', sortKey: null, width: '6%', thClass: 'text-center', hiddenLabel: true }
]

const activeTable = reactive(useSortedBatch(activeUsers, sortUserRows, DEFAULT_USER_SORT))
const pendingTable = reactive(useSortedBatch(pendingUsers, sortUserRows, DEFAULT_USER_SORT))

const emit = defineEmits(['showAlert'])

const activeTab = ref('active')
const addUserModalRef = ref(null)
const confirmModalRef = ref(null)
const removeUserModalRef = ref(null)
const userToRemove = ref(null)

function showAlert(message) {
  emit('showAlert', message)
}

// Confirmation modal state
const confirmModalTitle = ref('')
const confirmModalBody = ref('')
const confirmModalLabel = ref('Confirm')
const isConfirmProcessing = ref(false)
let pendingConfirmAction = null

function openConfirmModal({ title, body, confirmLabel, onConfirm }) {
  confirmModalTitle.value = title
  confirmModalBody.value = body
  confirmModalLabel.value = confirmLabel || 'Confirm'
  pendingConfirmAction = onConfirm
  isConfirmProcessing.value = false
  confirmModalRef.value.openModal()
}

async function onConfirmAction() {
  if (!pendingConfirmAction) return
  isConfirmProcessing.value = true
  try {
    await pendingConfirmAction()
  } finally {
    isConfirmProcessing.value = false
    confirmModalRef.value.closeModal()
    pendingConfirmAction = null
  }
}

function formatDate(value) {
  if (!value) return 'N/a'
  const date = value.toDate ? value.toDate() : new Date(value)
  if (isNaN(date.getTime())) return 'N/a'
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

const currentUid = localStorage.getItem('userUid')

function getDropdownActions(user) {
  const isSelf = user.id === currentUid
  const isAlreadyAdmin = isAgencyAdminUser(user)
  return [
    {
      key: 'allowAdmin',
      label: 'Allow Admin Rights',
      disabled: isAlreadyAdmin,
      tooltipText: isAlreadyAdmin ? 'This user already has admin rights' : ''
    },
    {
      key: 'removeUser',
      label: 'Remove User',
      disabled: isSelf,
      tooltipText: isSelf ? 'You cannot remove your own account' : '',
      variant: 'danger'
    }
  ]
}

function getPendingDropdownActions() {
  return [
    { key: 'resendInvite', label: 'Send Invite Nudge' },
    { key: 'cancelInvite', label: 'Cancel Invite', variant: 'danger' }
  ]
}

function onDropdownAction(action, user) {
  if (action === 'allowAdmin') {
    openConfirmModal({
      title: 'Grant Admin Access',
      body: `Are you sure you want to grant admin rights to ${user.fullname}? This will allow them to access the Admin Center and manage users.`,
      confirmLabel: 'Grant Admin Rights',
      onConfirm: async () => {
        try {
          await adminCenterStore.grantAgencyAdmin(user.id)
          showAlert(`Admin rights granted to ${user.fullname} successfully!`)
        } catch (error) {
          handleErrors('ManageUsers.allowAdmin', error)
        }
      }
    })
  } else if (action === 'removeUser') {
    userToRemove.value = user
    removeUserModalRef.value.openModal()
  } else if (action === 'resendInvite') {
    openConfirmModal({
      title: 'Resend Invite',
      body: `Are you sure you want to resend the password creation email to ${user.fullname} (${user.email})?`,
      confirmLabel: 'Resend Invite',
      onConfirm: async () => {
        try {
          await adminCenterStore.resendInvite(user.id)
          showAlert(`Invite resent to ${user.email} successfully!`)
        } catch (error) {
          handleErrors('ManageUsers.resendInvite', error)
        }
      }
    })
  } else if (action === 'cancelInvite') {
    openConfirmModal({
      title: 'Cancel Invite',
      body: `Are you sure you want to cancel the invite for ${user.fullname} (${user.email})? This will permanently remove the user from the system.`,
      confirmLabel: 'Cancel Invite',
      onConfirm: async () => {
        try {
          await adminCenterStore.cancelInvite(user.id)
          showAlert(`Invite for ${user.fullname} has been cancelled successfully!`)
        } catch (error) {
          handleErrors('ManageUsers.cancelInvite', error)
        }
      }
    })
  }
}

async function onRemoveConfirm({ user, reassignTo }) {
  try {
    await adminCenterStore.deactivateUser(user.id, reassignTo)
    removeUserModalRef.value.closeModal()
    showAlert(`${user.fullname} has been removed successfully!`)
  } catch (error) {
    handleErrors('ManageUsers.removeUser', error)
    removeUserModalRef.value.setProcessing(false)
  }
}

function openAddUserModal() {
  addUserModalRef.value.openModal()
}

async function onInviteSubmit(formData) {
  try {
    await adminCenterStore.createUser(formData)
    addUserModalRef.value.closeModal()
    showAlert('Account invite sent successfully!')
  } catch (error) {
    if (error.code === 'user_deactivated') {
      addUserModalRef.value.setEmailError(error.message)
    } else if (error.code === 'Email already in use' || error.message === 'Email already in use') {
      addUserModalRef.value.setEmailError('This email is already associated with an existing account.')
    } else {
      handleErrors('ManageUsers.onInviteSubmit', error)
      addUserModalRef.value.closeModal()
    }
  }
}
</script>

<template>
  <BcsCard title="Manage Users">
    <template #header-right>
      <div class="d-flex align-items-center gap-2">
        <button
          class="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
          type="button"
          id="manageUsersRefresh"
          :disabled="isLoading"
          @click="adminCenterStore.refreshUsers()"
        >
          <i class="bi bi-arrow-clockwise d-flex py-1" :class="{ 'spin-animation': isLoading }"></i>
        </button>
        <button class="btn btn-primary btn-sm d-flex align-items-center gap-1" type="button" @click="openAddUserModal">
          <i class="bi bi-plus-square d-flex"></i>
          Add New User Account
        </button>
      </div>
    </template>

    <div class="px-3 pt-4">
      <!-- Tabs -->
      <ul class="nav nav-tabs mb-0 border-0">
        <li class="nav-item">
          <button
            class="nav-link manage-users-tab text-16"
            :class="{ active: activeTab === 'active' }"
            @click="activeTab = 'active'"
            type="button"
          >
            Active Users ({{ totalActive }})
          </button>
        </li>
        <li class="nav-item">
          <button
            class="nav-link manage-users-tab text-16"
            :class="{ active: activeTab === 'pending' }"
            @click="activeTab = 'pending'"
            type="button"
          >
            Pending Invites ({{ totalPending }})
          </button>
        </li>
      </ul>
    </div>

      <!-- Active Users Tab -->
      <div v-if="activeTab === 'active'">
        <div v-if="isLoading" class="text-center py-4">
          <div class="spinner-border spinner-border-sm text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
        <table v-else id="manageUsersTable" class="table manage-users-table align-middle mb-0">
          <colgroup>
            <col v-for="column in ACTIVE_COLUMNS" :key="column.id" :style="{ width: column.width }" />
          </colgroup>
          <thead>
            <tr class="text-16">
              <th
                v-for="column in ACTIVE_COLUMNS"
                :key="column.id"
                class="fw-bold py-3"
                :class="column.thClass"
                :aria-sort="activeTable.ariaSort(column)"
                :data-column="column.id"
              >
                <button
                  v-if="column.sortKey"
                  type="button"
                  class="column-sort"
                  @click="activeTable.toggleSort(column.sortKey)"
                >
                  <span>{{ column.label }}</span>
                  <span class="column-sort__icon" :class="{ 'is-active': activeTable.isSortedBy(column) }">
                    <i class="bi" :class="activeTable.sortIconClass(column)"></i>
                  </span>
                </button>
                <span v-else :class="{ 'visually-hidden': column.hiddenLabel }">{{ column.label }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in activeTable.visibleRows" :key="user.id" class="text-16">
              <td class="ps-4">{{ user.fullname }}</td>
              <td>{{ user.role || 'N/a' }}</td>
              <td class="text-center">
                <BcsStatPill :value="user.employerCount" size="sm" />
              </td>
              <td class="text-center text-14">
                <BcsStatusPill text="Active" color="success" :bold="false"/>
              </td>
              <td class="text-center">
                <BcsDropdownActions
                  :item-id="user.id"
                  :actions="getDropdownActions(user)"
                  @action-click="onDropdownAction($event, user)"
                />
              </td>
            </tr>
            <tr v-if="activeTable.sortedRows.length === 0 && !isLoading">
              <td colspan="5" class="text-center text-muted py-3">No active users found.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pending Invites Tab -->
      <div v-else-if="activeTab === 'pending'">
        <div v-if="isLoading" class="text-center py-4">
          <div class="spinner-border spinner-border-sm text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
        <table v-else id="manageUsersTable" class="table manage-users-table align-middle mb-0">
            <colgroup>
              <col v-for="column in PENDING_COLUMNS" :key="column.id" :style="{ width: column.width }" />
            </colgroup>
          <thead>
            <tr class="text-16">
              <th
                v-for="column in PENDING_COLUMNS"
                :key="column.id"
                class="fw-bold py-3"
                :class="column.thClass"
                :aria-sort="pendingTable.ariaSort(column)"
                :data-column="column.id"
              >
                <button
                  v-if="column.sortKey"
                  type="button"
                  class="column-sort"
                  @click="pendingTable.toggleSort(column.sortKey)"
                >
                  <span>{{ column.label }}</span>
                  <span class="column-sort__icon" :class="{ 'is-active': pendingTable.isSortedBy(column) }">
                    <i class="bi" :class="pendingTable.sortIconClass(column)"></i>
                  </span>
                </button>
                <span v-else :class="{ 'visually-hidden': column.hiddenLabel }">{{ column.label }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in pendingTable.visibleRows" :key="user.id" class="text-16">
              <td class="ps-4">{{ user.fullname }}</td>
              <td>{{ user.role || 'N/a' }}</td>
              <td>{{ formatDate(user.lastInviteSentAt || user.authCreatedAt) }}</td>
              <td class="text-center">
                <BcsStatusPill text="Invite Pending" color="warning" :bold="false" />
              </td>
              <td class="text-center">
                <BcsDropdownActions
                  :item-id="user.id"
                  :actions="getPendingDropdownActions(user)"
                  @action-click="onDropdownAction($event, user)"
                />
              </td>
            </tr>
            <tr v-if="pendingTable.sortedRows.length === 0 && !isLoading">
              <td colspan="5" class="text-center text-muted py-3">No pending invites found.</td>
            </tr>
          </tbody>
        </table>
      </div>

    <AddAccountUserModal
      ref="addUserModalRef"
      @submit="onInviteSubmit"
    />

    <ConfirmActionModal
      ref="confirmModalRef"
      :title="confirmModalTitle"
      :body="confirmModalBody"
      :confirmLabel="confirmModalLabel"
      :isProcessing="isConfirmProcessing"
      @confirm="onConfirmAction"
    />

    <RemoveUserModal
      ref="removeUserModalRef"
      :user="userToRemove"
      :activeUsers="activeUsers"
      @confirm="onRemoveConfirm"
    />
  </BcsCard>

  <!-- Load More (outside card). The batch is a slice of the already-sorted full list. -->
  <div v-if="activeTab === 'active' && !isLoading && activeTable.sortedRows.length > 0" id="manageUsersLoadMore" class="text-start mt-3">
    <p class="text-14 mb-0" style="color: var(--bs-gray-700)">
      You're viewing {{ activeTable.visibleRows.length }} of {{ activeTable.sortedRows.length }} active users
    </p>
    <button
      v-if="activeTable.hasMore"
      id="manageUsersLoadMoreButton"
      type="button"
      class="btn btn-info text-16 mt-2"
      @click="activeTable.loadMore()"
    >
      <med>Load More Users</med>
      <i class="bi bi-chevron-double-down ms-2"></i>
    </button>
  </div>
  <div v-if="activeTab === 'pending' && !isLoading && pendingTable.sortedRows.length > 0" id="manageUsersLoadMore" class="text-start mt-3">
    <p class="text-14 mb-0" style="color: var(--bs-gray-700)">
      You're viewing {{ pendingTable.visibleRows.length }} of {{ pendingTable.sortedRows.length }} pending invites
    </p>
    <button
      v-if="pendingTable.hasMore"
      id="manageUsersLoadMoreButton"
      type="button"
      class="btn btn-info text-16 mt-2"
      @click="pendingTable.loadMore()"
    >
      <med>Load More Users</med>
      <i class="bi bi-chevron-double-down ms-2"></i>
    </button>
  </div>

</template>

<style scoped lang="scss">
:deep(.card-body) {
  padding: 0;
}

.manage-users-tab {
  color: var(--bs-primary) !important;
  font-weight: 500 !important;
  border: 1px solid var(--bs-border-color);
  border-bottom: none;
  background-color: transparent;

  &.active {
    color: var(--bs-secondary) !important;
    background-color: var(--bs-white);
    border-bottom-color: var(--bs-white);    
  }

  &:hover:not(.active) {
    color: var(--bs-primary);
  }
}

.manage-users-table {
  border-collapse: collapse;

  th, td {
    border-left: none;
    border-right: none;
  }

  thead th {
    background-color: var(--bs-white);
    border-bottom: 2px solid var(--bs-border-color);
    border-top: 1px solid var(--bs-border-color);
  }

  tbody td {
    border-bottom: 1px solid var(--bs-border-color);
  }

  tbody tr:last-child td {
    border-bottom: 1px solid var(--bs-border-color);
  }
}

.column-sort {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--bs-primary);
    outline-offset: 2px;
    border-radius: 4px;
  }
}

.column-sort__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  color: var(--bs-primary);
  font-size: 14px;

  &.is-active {
    background-color: #f7f1fd;
  }
}

.spin-animation {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
